import {
  CodeCommitClient,
  GetCommitCommand,
  GetDifferencesCommand,
  GetBlobCommand,
} from "@aws-sdk/client-codecommit";
import { updateItem, Tables } from "./db";
import { getAllCommits } from "./commits";
import type { Commit, ReviewStatus } from "../types";

const REGION = "us-east-1";
const REPO_NAME = "pos-system";

/** Get the CodeCommit client — uses credentials from the current session */
function getClient(): CodeCommitClient {
  // Import the configured client from commits service
  const stored = localStorage.getItem("devportal-auth-session");
  if (stored) {
    const session = JSON.parse(stored);
    return new CodeCommitClient({
      region: REGION,
      credentials: {
        accessKeyId: session.accessKeyId,
        secretAccessKey: session.secretAccessKey,
      },
    });
  }
  return new CodeCommitClient({ region: REGION });
}

/** File diff representation */
export interface FileDiff {
  filePath: string;
  changeType: "A" | "M" | "D"; // Added, Modified, Deleted
  beforeContent?: string;
  afterContent?: string;
  hunks: DiffHunk[];
}

export interface DiffHunk {
  oldStart: number;
  oldLines: number;
  newStart: number;
  newLines: number;
  lines: DiffLine[];
}

export interface DiffLine {
  type: "context" | "add" | "remove";
  content: string;
  oldLineNumber?: number;
  newLineNumber?: number;
}

/**
 * Get the diff for a specific commit (compared to its parent).
 * Returns file-level diffs with line-by-line changes and context.
 */
export async function getCommitDiff(commitId: string): Promise<FileDiff[]> {
  const cc = getClient();

  // Get the commit to find its parent
  const commitResp: any = await cc.send(
    new GetCommitCommand({ repositoryName: REPO_NAME, commitId })
  );
  const parentCommitId = commitResp.commit?.parents?.[0];

  // Get file differences
  const diffResp = await cc.send(
    new GetDifferencesCommand({
      repositoryName: REPO_NAME,
      beforeCommitSpecifier: parentCommitId || commitId,
      afterCommitSpecifier: commitId,
    })
  );

  const diffs: FileDiff[] = [];

  for (const diff of diffResp.differences || []) {
    const filePath = diff.afterBlob?.path || diff.beforeBlob?.path || "unknown";
    let changeType: "A" | "M" | "D" = "M";
    if (!diff.beforeBlob?.blobId) changeType = "A";
    else if (!diff.afterBlob?.blobId) changeType = "D";

    let beforeContent: string | undefined;
    let afterContent: string | undefined;

    // Fetch blob contents
    try {
      if (diff.beforeBlob?.blobId) {
        const blob = await cc.send(new GetBlobCommand({ repositoryName: REPO_NAME, blobId: diff.beforeBlob.blobId }));
        beforeContent = new TextDecoder().decode(blob.content);
      }
      if (diff.afterBlob?.blobId) {
        const blob = await cc.send(new GetBlobCommand({ repositoryName: REPO_NAME, blobId: diff.afterBlob.blobId }));
        afterContent = new TextDecoder().decode(blob.content);
      }
    } catch {
      // Binary file or error — skip content
    }

    // Generate unified diff hunks
    const hunks = generateDiffHunks(beforeContent || "", afterContent || "", 3);

    diffs.push({ filePath, changeType, beforeContent, afterContent, hunks });
  }

  return diffs;
}

/**
 * Simple line-level diff algorithm with context lines.
 * Produces hunks similar to unified diff format.
 */
function generateDiffHunks(before: string, after: string, contextLines: number = 3): DiffHunk[] {
  const oldLines = before.split("\n");
  const newLines = after.split("\n");

  // Simple LCS-based diff
  const changes = computeLineChanges(oldLines, newLines);

  // Group changes into hunks with context
  const hunks: DiffHunk[] = [];
  let currentHunk: DiffHunk | null = null;
  let lastChangeIdx = -999;

  for (let i = 0; i < changes.length; i++) {
    const change = changes[i];

    if (change.type !== "context") {
      // Start new hunk if too far from last change
      if (i - lastChangeIdx > contextLines * 2 + 1) {
        if (currentHunk) hunks.push(currentHunk);
        // Add leading context
        const contextStart = Math.max(0, i - contextLines);
        currentHunk = {
          oldStart: changes[contextStart]?.oldLineNumber || 1,
          newStart: changes[contextStart]?.newLineNumber || 1,
          oldLines: 0,
          newLines: 0,
          lines: [],
        };
        for (let j = contextStart; j < i; j++) {
          currentHunk.lines.push(changes[j]);
          if (changes[j].type !== "add") currentHunk.oldLines++;
          if (changes[j].type !== "remove") currentHunk.newLines++;
        }
      } else if (!currentHunk) {
        const contextStart = Math.max(0, i - contextLines);
        currentHunk = {
          oldStart: changes[contextStart]?.oldLineNumber || 1,
          newStart: changes[contextStart]?.newLineNumber || 1,
          oldLines: 0,
          newLines: 0,
          lines: [],
        };
        for (let j = contextStart; j < i; j++) {
          currentHunk.lines.push(changes[j]);
          if (changes[j].type !== "add") currentHunk.oldLines++;
          if (changes[j].type !== "remove") currentHunk.newLines++;
        }
      }

      lastChangeIdx = i;
    }

    if (currentHunk) {
      // Add trailing context (within range)
      if (change.type === "context" && i - lastChangeIdx <= contextLines) {
        currentHunk.lines.push(change);
        currentHunk.oldLines++;
        currentHunk.newLines++;
      } else if (change.type !== "context") {
        currentHunk.lines.push(change);
        if (change.type !== "add") currentHunk.oldLines++;
        if (change.type !== "remove") currentHunk.newLines++;
      } else if (i - lastChangeIdx > contextLines) {
        // End of trailing context — close hunk
        hunks.push(currentHunk);
        currentHunk = null;
      }
    }
  }

  if (currentHunk && currentHunk.lines.length > 0) {
    hunks.push(currentHunk);
  }

  return hunks;
}

/**
 * Compute line-level changes between two arrays of lines.
 * Returns a flat list of context/add/remove lines.
 */
function computeLineChanges(oldLines: string[], newLines: string[]): DiffLine[] {
  const result: DiffLine[] = [];
  let oldIdx = 0;
  let newIdx = 0;

  // Simple O(n) approach: walk both sides, match identical lines
  while (oldIdx < oldLines.length || newIdx < newLines.length) {
    if (oldIdx < oldLines.length && newIdx < newLines.length && oldLines[oldIdx] === newLines[newIdx]) {
      result.push({ type: "context", content: oldLines[oldIdx], oldLineNumber: oldIdx + 1, newLineNumber: newIdx + 1 });
      oldIdx++;
      newIdx++;
    } else {
      // Look ahead in new lines for a match to the current old line
      let foundInNew = -1;
      for (let j = newIdx; j < Math.min(newIdx + 10, newLines.length); j++) {
        if (oldIdx < oldLines.length && newLines[j] === oldLines[oldIdx]) {
          foundInNew = j;
          break;
        }
      }

      let foundInOld = -1;
      for (let j = oldIdx; j < Math.min(oldIdx + 10, oldLines.length); j++) {
        if (newIdx < newLines.length && oldLines[j] === newLines[newIdx]) {
          foundInOld = j;
          break;
        }
      }

      if (foundInNew >= 0 && (foundInOld < 0 || foundInNew - newIdx <= foundInOld - oldIdx)) {
        // Lines were added
        while (newIdx < foundInNew) {
          result.push({ type: "add", content: newLines[newIdx], newLineNumber: newIdx + 1 });
          newIdx++;
        }
      } else if (foundInOld >= 0) {
        // Lines were removed
        while (oldIdx < foundInOld) {
          result.push({ type: "remove", content: oldLines[oldIdx], oldLineNumber: oldIdx + 1 });
          oldIdx++;
        }
      } else {
        // No match found — treat as remove old + add new
        if (oldIdx < oldLines.length) {
          result.push({ type: "remove", content: oldLines[oldIdx], oldLineNumber: oldIdx + 1 });
          oldIdx++;
        }
        if (newIdx < newLines.length) {
          result.push({ type: "add", content: newLines[newIdx], newLineNumber: newIdx + 1 });
          newIdx++;
        }
      }
    }
  }

  return result;
}

// --- Review Actions ---

/** Get all commits that are pending review (in order) */
export async function getPendingReviews(): Promise<Commit[]> {
  const commits = await getAllCommits();
  return commits
    .filter((c) => c.reviewStatus === "pending")
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()); // oldest first
}

/** Get all reviewed commits */
export async function getReviewedCommits(): Promise<Commit[]> {
  const commits = await getAllCommits();
  return commits
    .filter((c) => c.reviewStatus === "approved" || c.reviewStatus === "changes-requested")
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

/** Check if a commit can be reviewed — always true unless a previous commit has changes-requested */
/** Check if a commit can be reviewed (previous commits with changes-requested block it) */
export async function canReviewCommit(_commitId: string): Promise<boolean> {
  const commits = await getAllCommits();
  const sorted = commits.sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  for (const commit of sorted) {
    if (commit.commitId === _commitId) return true;
    if (commit.reviewStatus === "changes-requested") return false;
  }
  return true;
}

/** Approve a commit */
export async function approveCommit(commitId: string, reviewerUsername: string, comment?: string): Promise<void> {
  await updateItem(Tables.COMMITS, { commitId }, {
    reviewStatus: "approved" as ReviewStatus,
    reviewedBy: reviewerUsername,
    reviewedAt: new Date().toISOString(),
    reviewComment: comment || "",
  });
}

/** Request changes on a commit */
export async function requestChanges(commitId: string, reviewerUsername: string, comment: string): Promise<void> {
  await updateItem(Tables.COMMITS, { commitId }, {
    reviewStatus: "changes-requested" as ReviewStatus,
    reviewedBy: reviewerUsername,
    reviewedAt: new Date().toISOString(),
    reviewComment: comment,
  });
}
